/**
 * JdSharedWithRow — the JD page header's "Draft shared with" row (wireframe jobs-library-ownership-v3.html; Ownership
 * design, part 3): a name chip per person the private drafts are shared with, shown to everyone. Whoever holds the
 * JD (the delegate while delegated, otherwise the owner) stops sharing with × and shares with + (the picker) while
 * fewer than 10 people have it, never while the JD is in review or archived (the label's tooltip then says why; ×
 * stays). Nobody manages it while editing. Renders nothing when nobody is shared with and the
 * viewer can't add anyone. With more than 2 people only the first 2 show (the viewer first when shared), then a
 * "+N more" chip that expands the full list in place ("Show less" folds it), so the header doesn't grow by default.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SbTooltip, TooltipTriggerStyled } from 'uiCore';
import { getInitials } from 'Pages/AdminSetup/components/LegalEntityManagement/helper';
import {
  NO_ONE_LABEL, SHARED_WITH_LABEL, SHOW_LESS_LABEL, SHARED_SHOWN, TOOLTIP_SHARE_ADD, YOU_SUFFIX, getMoreLabel,
  getSharedRow, getStopSharingTooltip, orderSharedPeople,
} from 'Utils/jdOwnership';
import {
  ChipAddStyled, ChipRemoveStyled, NoOneStyled, OwnerLabelStyled, OwnerRowStyled, PersonAvatarStyled, PersonChipStyled,
} from './JdCurrentOwnerRow.styles';

const JdSharedWithRow = ({
  jdOwnership = null, viewerEmail = '', canManage = false, isEditing = false, busy = false, onAdd, onRemove,
}) => {
  const [expanded, setExpanded] = useState(false);
  const row = getSharedRow(jdOwnership, { canManage, isEditing, viewerEmail });
  if (!row) return null;
  const people = orderSharedPeople(row.people);
  const hidden = people.length - SHARED_SHOWN;
  const shown = expanded || hidden <= 0 ? people : people.slice(0, SHARED_SHOWN);
  return (
    <OwnerRowStyled data-testid="jd-shared-with">
      {row.blockedTooltip ? (
        <SbTooltip content={row.blockedTooltip}>
          <TooltipTriggerStyled>
            <OwnerLabelStyled>{SHARED_WITH_LABEL}</OwnerLabelStyled>
          </TooltipTriggerStyled>
        </SbTooltip>
      ) : (
        <OwnerLabelStyled>{SHARED_WITH_LABEL}</OwnerLabelStyled>
      )}
      {people.length ? shown.map((person) => (
        <PersonChipStyled key={person.email}>
          <PersonAvatarStyled aria-hidden="true">{getInitials(person.name)}</PersonAvatarStyled>
          {person.name}
          {person.isYou && ` ${YOU_SUFFIX}`}
          {row.canRemove && (
            <SbTooltip content={getStopSharingTooltip(person.name)}>
              <TooltipTriggerStyled>
                <ChipRemoveStyled
                  type="button"
                  aria-label={getStopSharingTooltip(person.name)}
                  disabled={busy}
                  onClick={() => onRemove(person.email, person.name)}
                >
                  ×
                </ChipRemoveStyled>
              </TooltipTriggerStyled>
            </SbTooltip>
          )}
        </PersonChipStyled>
      )) : (
        <NoOneStyled>{NO_ONE_LABEL}</NoOneStyled>
      )}
      {hidden > 0 && (
        <PersonChipStyled as="button" type="button" aria-expanded={expanded} style={{ cursor: 'pointer' }}
          onClick={() => setExpanded((v) => !v)}>
          {expanded ? SHOW_LESS_LABEL : getMoreLabel(hidden)}
        </PersonChipStyled>
      )}
      {row.canAdd && (
        <SbTooltip content={TOOLTIP_SHARE_ADD}>
          <TooltipTriggerStyled>
            <ChipAddStyled type="button" aria-label="Share drafts" disabled={busy} onClick={onAdd}>+</ChipAddStyled>
          </TooltipTriggerStyled>
        </SbTooltip>
      )}
    </OwnerRowStyled>
  );
};

JdSharedWithRow.propTypes = {
  jdOwnership: PropTypes.shape({
    isOwner: PropTypes.bool,
    isDelegate: PropTypes.bool,
    familyStatus: PropTypes.string,
    shares: PropTypes.arrayOf(PropTypes.shape({ email: PropTypes.string, name: PropTypes.string })),
  }),
  viewerEmail: PropTypes.string,
  canManage: PropTypes.bool,
  isEditing: PropTypes.bool,
  busy: PropTypes.bool,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
};

export default JdSharedWithRow;
